import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Modal, TextInput } from '@opendate-app/setlist';
import maskAmount from '../utils/maskAmount';

const axios = require('axios').default;

export default function FairMarketValueModal({
  donation, path, csrfToken, onClose, onSaved,
}) {
  const [fairMarketValue, setFairMarketValue] = useState(
    donation.fair_market_value_value != null ? String(donation.fair_market_value_value) : '',
  );
  const [description, setDescription] = useState(donation.goods_services_description || '');
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const receivedGoodsOrServices = parseFloat(fairMarketValue) > 0;

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setErrors([]);
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    try {
      await axios.patch(`${path}.json`, {
        donation: {
          fair_market_value: fairMarketValue || null,
          goods_services_description: receivedGoodsOrServices ? description : null,
        },
      });
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setErrors(err.response?.data?.errors || ['Failed to save. Please try again.']);
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
      <Button onClick={handleSave} disabled={saving} data-testid="fair-market-value-submit">
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Benefits received"
      description={`Gift #${donation.id} of ${donation.gift_amount_value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}. Record the value of anything the donor received in return, such as a gala dinner or event seats.`}
      footer={footer}
      data-testid="fair-market-value-modal"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {errors.length > 0 && (
          <div data-testid="fair-market-value-errors">
            <Alert color="error" title={errors.join(' ')} />
          </div>
        )}
        <TextInput
          label="Fair market value of goods or services"
          leadingText="$"
          inputMode="decimal"
          placeholder="0.00"
          hint="Leave blank to record that the donor received nothing in return."
          value={fairMarketValue}
          onChange={(e) => setFairMarketValue(maskAmount(e.target.value))}
          data-testid="fmv-fmv"
        />
        {receivedGoodsOrServices && (
          <TextInput
            label="Goods or services provided"
            required
            placeholder="e.g. Gala dinner for two"
            maxLength={255}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="fmv-goods-services"
          />
        )}
      </div>
    </Modal>
  );
}

FairMarketValueModal.propTypes = {
  donation: PropTypes.shape({
    id: PropTypes.number.isRequired,
    gift_amount_value: PropTypes.number.isRequired,
    fair_market_value_value: PropTypes.number,
    goods_services_description: PropTypes.string,
  }).isRequired,
  path: PropTypes.string.isRequired,
  csrfToken: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};
